<template>
  <div id="users" class="flex flex-col gap-6 proxima_nova">
    <div id="users_header" class="flex flex-row justify-between align-center">
      <h1 class="text-xl text_color font-semibold">Users</h1>
      <a-button class="primary_medium" @click="showUserModal"
        >Add User</a-button
      >
    </div>

    <div id="users_body" class="flex flex-col gap-6">
      <div class="flex flex-col gap-2 items-start w-1/2">
        <span class="text-sm font-normal"
          >Email addresses with these domains can join your workspace
          directly</span
        >

        <a-select
          v-model:value="domainsSelected"
          mode="tags"
          placeholder="Enter Domains"
          @change="handleDomainChange"
          class="w-full"
          :show-arrow="false"
          :open="false"
          :tokenSeparators="[',', ' ', 'Enter']"
          ref="enterDomainsInputRef"
        ></a-select>
      </div>

      <div id="users_table">
        <SkeletonLoader v-if="loadingData" :page="loaderPages.TABLE" />

        <CustomTable
          v-else-if="users?.length > 0"
          :headers="userHeaders"
          :data="users"
          :pagination="pagination"
          :custom-column-render="customColumnRender"
          @update:pagination="handlePageChange"
        />

        <EmptyScreen v-else source="users" />
      </div>
    </div>

    <div id="users_actions">
      <UserModal
        v-model="userModalVisible"
        :is-editing="false"
        :roles="roles"
        :users="users"
        :user-count="userCount"
      />
    </div>
  </div>
</template>

<script setup>
import { nextTick, onMounted, onUnmounted, reactive, ref, watch } from "vue";
import { message } from "ant-design-vue";
import { isDomainValid } from "@/src/utils/utils.js";
import { focusDelay } from "@/src/constants/commonConstants.js";
import { loaderPages } from "@/src/constants/loaderConstants.js";
import { restrictedBaseDomains } from "@/src/constants/domainConstants.js";
import { userHeaders } from "@/src/constants/userConstants.js";
import UserActions from "@/src/views/users/UserActions.vue";
import CreditLimitCell from "@/src/views/users/CreditLimitCell.vue";
import eventBus from "@/src/utils/eventBus.js";
import DashboardService from "@/src/api/DashboardService.js";
import AuthPermissionService from "@/src/api/AuthPermissionService.js";
import BillingServiceV4 from "@/src/api/BillingServiceV4.js";
import { isABACEnabled } from "@/src/utils/dynamicFormUtils.js";
import UserModal from "@/src/views/users/UserModal.vue";
import EmptyScreen from "@/src/components/EmptyScreen.vue";
import SkeletonLoader from "@/src/components/SkeletonLoader.vue";
import CustomTable from "@/src/components/custom_table_components/CustomTable.vue";

const customColumnRender = {
  pending: UserActions,
  credit_limit: CreditLimitCell,
};

const users = ref([]);
const roles = ref([]);
const loadingData = ref(false);
const domainsSelected = ref([]);
const userModalVisible = ref(false);
const enterDomainsInputRef = ref(null);
const userCount = ref(0);
const pagination = reactive({
  current: 1,
  pageSize: 20,
  total: 0,
});

const handlePageChange = (newPagination) => {
  try {
    pagination.value = newPagination;
    fetchUsers();
  } catch (error) {
    console.error("An error occurred while handling page change: ", error);
  }
};

const showUserModal = () => {
  try {
    userModalVisible.value = true;
  } catch (error) {
    console.error("An error occurred while showing user modal: ", error);
  }
};

const handleFocus = (ref) => {
  try {
    nextTick(() => {
      setTimeout(() => {
        if (ref?.value) {
          ref.value?.focus();
        }
      }, focusDelay);
    });
  } catch (error) {
    console.error("An error occurred while handling focus: ", error);
  }
};

const fetchAllData = async () => {
  try {
    await Promise.all([fetchRoles(), fetchUsers(), fetchDomains()]);
  } catch (error) {
    console.error("Error loading data:", error);
  }
};

const isRestrictedDomain = (domain) => {
  try {
    const baseDomain = domain.split(".")[0];
    return restrictedBaseDomains.includes(baseDomain);
  } catch (error) {
    console.error(
      "An error occurred for checking if its restricted domain: ",
      error
    );
  }
};

const extractDomainFromEmail = (email) => {
  try {
    return email.split("@")[1]?.toLowerCase();
  } catch (error) {
    console.error(
      "An error occurred while extracting domain from email: ",
      error
    );
  }
};

const isDomainInUsersList = (domain) => {
  try {
    return users.value.some((user) => {
      const userDomain = extractDomainFromEmail(user.email);
      return userDomain === domain;
    });
  } catch (error) {
    console.error(
      "An error occurred while checking domain is in users list: ",
      error
    );
  }
};

const handleDomainChange = (value) => {
  try {
    domainsSelected.value = value.filter((domain) => {
      const cleanDomain = domain.toLowerCase().trim();

      if (!isDomainValid(cleanDomain)) {
        message.error("Please add a valid domain");
        return false;
      }

      if (isRestrictedDomain(cleanDomain)) {
        message.error(`${cleanDomain} is not allowed as a workspace domain`);
        return false;
      }

      if (!isDomainInUsersList(cleanDomain)) {
        message.error(
          `One user with "@${cleanDomain}" email should be part of this workspace to allow this domain`
        );
        return false;
      }

      return true;
    });

    updateDomainToJoin();
  } catch (error) {
    console.error("An error occurred while handling domain change: ", error);
  }
};

const updateDomainToJoin = async () => {
  try {
    const payload = { domains: domainsSelected.value };
    const response = await DashboardService.updateDomainsForWorkspace(payload);
    if (response?.success) {
      domainsSelected.value = response?.domains;
    }
  } catch (err) {
    console.error("Error occurred while updating domains in workspace", err);
  }
};

const fetchDomains = async () => {
  try {
    const response = await DashboardService.getDomainsForWorkspace();
    if (response?.success) {
      domainsSelected.value = response?.domains;
    }
  } catch (error) {
    console.error("Error fetching domains:", error);
  }
};

const fetchRoles = async () => {
  try {
    let response;

    if (isABACEnabled()) {
      response = await AuthPermissionService.fetchRoles();
      if (response?.data) {
        roles.value = response.data.map((item) => ({
          id: item.id,
          role_name: item.attributes.name,
        }));
      }
    } else {
      response = await DashboardService.fetchRoles();
    if (response.success) {
      roles.value = response.roles;
      }
    }
  } catch (error) {
    message.error("Failed to fetch roles");
  }
};

const fetchUsers = async () => {
  try {
    loadingData.value = true;

    const response = await DashboardService.fetchUsers("active");
    const pendingResponse = await DashboardService.fetchPendingUsers();
    const expiredResponse = await DashboardService.fetchExpiredUserInvites();
    let pendingInviteCount = 0;
    if (
      response?.success &&
      pendingResponse?.success &&
      expiredResponse?.success
    ) {
      let allUsers = [
        ...response?.users,
        ...pendingResponse?.users,
        ...expiredResponse?.users,
      ];

      if (isABACEnabled()) {
        try {
          const roleMembershipsResponse = await AuthPermissionService.fetchRoleMemberships();
          if (roleMembershipsResponse?.data) {
            const userRoleMap = {};
            roleMembershipsResponse.data.forEach((membership) => {
              const userId = membership.attributes.user_id;
              const roleName = membership.attributes.role?.name;
              if (userId && roleName) {
                userRoleMap[userId] = roleName;
              }
            });

            allUsers = allUsers.map((user) => ({
              ...user,
              role_name: userRoleMap[user.id] || user.role_name,
            }));
          }
        } catch (error) {
          console.error("Error fetching ABAC role memberships:", error);
        }
      }

      const budgetByUserId = await fetchUserBudgets();
      users.value = allUsers.map((user) => ({
        ...user,
        credit_limit: resolveCreditLimit(budgetByUserId[user.id]),
      }));
      pendingInviteCount = pendingResponse?.users?.filter(
        (user) => user.pending_invite
      ).length;
      userCount.value = response.total_active + pendingInviteCount;
    }
  } catch (error) {
    console.error("Error fetching users:", error);
  } finally {
    loadingData.value = false;
  }
};

watch(
  userModalVisible,
  (isVisible) => {
    handleFocus(enterDomainsInputRef);
  },
  { immediate: true }
);

// Budgets: only users with a row are returned; missing = unlimited (default).
// is_enabled=false also means unlimited (soft-off — row preserved for potential re-enable).
const fetchUserBudgets = async () => {
  try {
    const response = await BillingServiceV4.listUserBudgets();
    const budgets = response?.budgets || [];
    return budgets.reduce((acc, b) => {
      acc[b.auth_user_id] = b;
      return acc;
    }, {});
  } catch (err) {
    console.error("Failed to fetch user budgets:", err);
    return {};
  }
};

const resolveCreditLimit = (budget) => {
  if (!budget || !budget.is_enabled) return null;
  return Number(budget.monthly_limit_credits);
};

const handleBudgetUpdated = ({ userId, credit_limit }) => {
  const idx = users.value.findIndex((u) => u.id === userId);
  if (idx >= 0) {
    users.value[idx] = { ...users.value[idx], credit_limit };
  }
};

onMounted(() => {
  fetchAllData();
  eventBus.on("user-altered", fetchUsers);
  eventBus.on("user-budget-updated", handleBudgetUpdated);
});

onUnmounted(() => {
  eventBus.off("user-altered", fetchUsers);
  eventBus.off("user-budget-updated", handleBudgetUpdated);
});
</script>
